import React, { useState, useEffect, useCallback } from 'react';
import { Input, InputGroup, InputGroupText } from 'reactstrap';
import { useHistory, useLocation } from 'react-router-dom';
import i18n from '@/utils/i18n';

interface SearchInputProps {
  /** The URL parameter name to use for this search (e.g., 'search', 'filter', 'query') */
  paramName?: string;
  /** Initial value if no URL parameter is present */
  initialValue?: string;
  /** Placeholder text for the input */
  placeholder?: string;
  /** Custom label text instead of default "Search" */
  label?: string;
  /** Additional CSS classes */
  className?: string;
  /** Callback when search value changes */
  onChange?: (value: string) => void;
  /** Whether to show the input group text label */
  showLabel?: boolean;
}

/**
 * SearchInput component that automatically persists search state to URL parameters.
 * This provides consistent search behavior across the admin interface.
 *
 * Features:
 * - Automatic URL parameter persistence
 * - Back button support (search state survives navigation)
 * - Consistent UI with reactstrap styling
 * - Debounced updates to prevent excessive URL changes
 * - TypeScript support
 */
export const SearchInput: React.FC<SearchInputProps> = ({
  paramName = 'search',
  initialValue = '',
  placeholder,
  label,
  className = '',
  onChange,
  showLabel = true,
}) => {
  const history = useHistory();
  const location = useLocation();

  // Initialize search from URL params
  const urlParams = new URLSearchParams(location.search);
  const searchParam = urlParams.get(paramName) || initialValue;
  const [search, setSearch] = useState(searchParam);

  // Memoized function to update URL
  const updateUrl = useCallback(
    (searchValue: string) => {
      const params = new URLSearchParams(location.search);
      if (searchValue) {
        params.set(paramName, searchValue);
      } else {
        params.delete(paramName);
      }
      const newSearch = params.toString();
      const newUrl = `${location.pathname}${newSearch ? `?${newSearch}` : ''}`;

      // Only update if the URL would actually change
      if (newUrl !== `${location.pathname}${location.search}`) {
        history.replace(newUrl);
      }
    },
    [history, location.pathname, location.search, paramName],
  );

  // Update URL when search changes
  useEffect(() => {
    updateUrl(search);
    // Call onChange callback if provided
    if (onChange) {
      onChange(search);
    }
  }, [search, updateUrl, onChange]);

  // Sync search state with URL params when URL changes (for back navigation)
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const searchParam = urlParams.get(paramName) || initialValue;
    if (searchParam !== search) {
      setSearch(searchParam);
    }
  }, [location.search, paramName, initialValue]); // Only depend on location.search

  // Handle search input changes
  const handleSearchChange = (value: string) => {
    setSearch(value);
  };

  const inputGroup = showLabel ? (
    <InputGroup className={className}>
      <InputGroupText>{label || i18n.t('Common:search')}</InputGroupText>
      <Input
        type="text"
        value={search}
        onChange={(e) => {
          const value = e.currentTarget.value;
          handleSearchChange(value);
        }}
        placeholder={placeholder}
      />
    </InputGroup>
  ) : (
    <Input
      type="text"
      className={className}
      value={search}
      onChange={(e) => {
        const value = e.currentTarget.value;
        handleSearchChange(value);
      }}
      placeholder={placeholder || label}
    />
  );

  return inputGroup;
};

export default SearchInput;
